import styles from "./StateMap.module.css";

// The US as a tile grid, one square per state, placed roughly where the state lies:
// [code, name, row, column] on 8 rows of 12.
const STATES = [
  ["ME", "Maine", 1, 12],
  ["WI", "Wisconsin", 2, 7],
  ["VT", "Vermont", 2, 11],
  ["NH", "New Hampshire", 2, 12],
  ["WA", "Washington", 3, 2],
  ["ID", "Idaho", 3, 3],
  ["MT", "Montana", 3, 4],
  ["ND", "North Dakota", 3, 5],
  ["MN", "Minnesota", 3, 6],
  ["IL", "Illinois", 3, 7],
  ["MI", "Michigan", 3, 8],
  ["NY", "New York", 3, 10],
  ["MA", "Massachusetts", 3, 11],
  ["OR", "Oregon", 4, 2],
  ["NV", "Nevada", 4, 3],
  ["WY", "Wyoming", 4, 4],
  ["SD", "South Dakota", 4, 5],
  ["IA", "Iowa", 4, 6],
  ["IN", "Indiana", 4, 7],
  ["OH", "Ohio", 4, 8],
  ["PA", "Pennsylvania", 4, 9],
  ["NJ", "New Jersey", 4, 10],
  ["CT", "Connecticut", 4, 11],
  ["RI", "Rhode Island", 4, 12],
  ["CA", "California", 5, 2],
  ["UT", "Utah", 5, 3],
  ["CO", "Colorado", 5, 4],
  ["NE", "Nebraska", 5, 5],
  ["MO", "Missouri", 5, 6],
  ["KY", "Kentucky", 5, 7],
  ["WV", "West Virginia", 5, 8],
  ["VA", "Virginia", 5, 9],
  ["MD", "Maryland", 5, 10],
  ["DE", "Delaware", 5, 11],
  ["AZ", "Arizona", 6, 3],
  ["NM", "New Mexico", 6, 4],
  ["KS", "Kansas", 6, 5],
  ["AR", "Arkansas", 6, 6],
  ["TN", "Tennessee", 6, 7],
  ["NC", "North Carolina", 6, 8],
  ["SC", "South Carolina", 6, 9],
  ["DC", "District of Columbia", 6, 10],
  ["OK", "Oklahoma", 7, 5],
  ["LA", "Louisiana", 7, 6],
  ["MS", "Mississippi", 7, 7],
  ["AL", "Alabama", 7, 8],
  ["GA", "Georgia", 7, 9],
  ["HI", "Hawaii", 8, 1],
  ["AK", "Alaska", 8, 2],
  ["TX", "Texas", 8, 5],
  ["FL", "Florida", 8, 10],
];

/** A state's full name from its two-letter code ("AR" → "Arkansas"); unknown codes pass through. */
export const stateName = (code) => STATES.find(([c]) => c === code)?.[1] ?? code;

const list = (names) =>
  names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names[0];

/**
 * Where the clients are: every state as a tile, solid where the state's DOT is a client,
 * outlined where other clients are. Hovering a tile names its clients; screen readers get
 * the same as one sentence. `clients` are { name, type, state }.
 */
export default function StateMap({ clients }) {
  const byState = new Map();
  for (const c of clients) {
    if (!c.state) continue;
    const s = byState.get(c.state) || { dot: false, names: [] };
    if (c.type === "dot") s.dot = true;
    s.names.push(c.name);
    byState.set(c.state, s);
  }
  const named = (dot) =>
    STATES.filter(([code]) => byState.has(code) && byState.get(code).dot === dot).map(
      ([, name]) => name
    );
  const summary =
    `Map of the United States. State DOT clients in ${list(named(true))}.` +
    (named(false).length ? ` Other clients in ${list(named(false))}.` : "");

  return (
    <figure className={styles.figure}>
      <div className={styles.grid} role="img" aria-label={summary}>
        {STATES.map(([code, name, row, col]) => {
          const s = byState.get(code);
          const kind = s ? (s.dot ? styles.dot : styles.other) : "";
          return (
            <span
              key={code}
              className={`${styles.tile} ${kind}`.trim()}
              style={{ gridRow: row, gridColumn: col }}
              title={s ? `${name}: ${s.names.join(", ")}` : name}
              aria-hidden="true"
            >
              {code}
            </span>
          );
        })}
      </div>
      <figcaption className={styles.legend} aria-hidden="true">
        <span className={styles.key}>
          <span className={`${styles.swatch} ${styles.dot}`} />
          State DOT client
        </span>
        <span className={styles.key}>
          <span className={`${styles.swatch} ${styles.other}`} />
          Other clients in the state
        </span>
      </figcaption>
    </figure>
  );
}
